import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AppShell, Center, Loader, Group, Title, Button, Anchor, Text, Container } from '@mantine/core';
import { IconBrandWordpress, IconServer, IconKey, IconPhoto } from '@tabler/icons-react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Sites from './pages/Sites';
import MCPStatus from './pages/MCPStatus';
import SSOCallback from './pages/SSOCallback';
import ApiKeys from './pages/ApiKeys';
import Media from './pages/Media';
import { useState, useEffect } from 'react';
import { authApi } from './api/client';

const NAV_LINKS = [
    { label: 'Sites', to: '/sites', icon: <IconBrandWordpress size={16} />, exact: false },
    { label: 'MCP Status', to: '/mcp-status', icon: <IconServer size={16} />, exact: false },
    { label: 'API Keys', to: '/api-keys', icon: <IconKey size={16} />, exact: false },
    { label: 'Media', to: '/media', icon: <IconPhoto size={16} />, exact: false },
];

function AppHeader({ username, onLogout }: { username: string | null; onLogout: () => void }) {
    const location = useLocation();
    return (
        <AppShell.Header>
            <Container size="xl" h="100%">
                <Group h="100%" justify="space-between">
                    <Group gap="xs">
                        <Anchor component={Link} to="/" underline="never" c="inherit">
                            <Title order={5}>WP MCP</Title>
                        </Anchor>
                        <Group gap={4} ml="sm">
                            {NAV_LINKS.map((link) => {
                                const isActive = link.exact
                                    ? location.pathname === link.to
                                    : location.pathname.startsWith(link.to);
                                return (
                                    <Button
                                        key={link.to}
                                        component={Link}
                                        to={link.to}
                                        variant={isActive ? 'light' : 'subtle'}
                                        size="xs"
                                        leftSection={link.icon}
                                    >
                                        {link.label}
                                    </Button>
                                );
                            })}
                        </Group>
                    </Group>
                    <Group gap="sm">
                        {username && (
                            <Text size="xs" c="dimmed">
                                {username}
                            </Text>
                        )}
                        <Button variant="subtle" size="xs" onClick={onLogout}>
                            Logout
                        </Button>
                    </Group>
                </Group>
            </Container>
        </AppShell.Header>
    );
}

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [username, setUsername] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkAuth = async () => {
        try {
            const data = await authApi.checkSession();
            setIsAuthenticated(true);
            setIsAdmin(data?.user?.role === 'admin');
            setUsername(data?.user?.username ?? null);
        } catch {
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    const handleLogout = async () => {
        try {
            await authApi.logout();
        } catch {
            // logout even if the API call fails
        }
        setIsAuthenticated(false);
        setUsername(null);
    };

    if (isLoading) {
        return (
            <Center style={{ height: '100vh' }}>
                <Loader size="lg" />
            </Center>
        );
    }

    if (!isAuthenticated) {
        const isSSORoute = window.location.pathname === '/sso';
        if (isSSORoute) {
            return (
                <BrowserRouter>
                    <Routes>
                        <Route path="/sso" element={<SSOCallback onLogin={checkAuth} />} />
                    </Routes>
                </BrowserRouter>
            );
        }
        return <Login onLogin={checkAuth} />;
    }

    return (
        <BrowserRouter>
            <AppShell header={{ height: 56 }} padding="md">
                <AppHeader username={username} onLogout={handleLogout} />
                <AppShell.Main>
                    <Routes>
                        <Route path="/" element={<Dashboard />} />
                        <Route path="/sites" element={<Sites isAdmin={isAdmin} />} />
                        <Route path="/mcp-status" element={<MCPStatus />} />
                        <Route path="/api-keys" element={<ApiKeys isAdmin={isAdmin} />} />
                        <Route path="/media" element={<Media />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </AppShell.Main>
            </AppShell>
        </BrowserRouter>
    );
}

export default App;
